import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, Paper, Button, Alert, Container, IconButton, useMediaQuery, useTheme } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useNavigate } from 'react-router-dom';
import Axios from "axios";
import MapComponent from './Map/MapComponent';
import { gsst_admlayers } from '../Constants/GeologicalOptions';
import StateContext from '../Contexts/StateContext';
import DispatchContext from '../Contexts/DispatchContext';
import DesktopFilter from './Feltarasok/DesktopFilter';
import MobileDrawer from './Feltarasok/MobileDrawer';
import config, { environment } from '../config';
import { useTranslation } from 'react-i18next';

function GSST() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const globalState = useContext(StateContext);
    const globalDispatch = useContext(DispatchContext);

    const [filterOpen, setFilterOpen] = useState(false);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedAdmLayers, setSelectedAdmLayers] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState({});

    // Data fetching effects for GSST (currently reusing parish, szek, megye)
    useEffect(() => {
        const source = Axios.CancelToken.source();
        async function getParishes(page = 1) {
            if (selectedAdmLayers.includes('kozsegvaros')) {
                try {
                    const endpoint = config[environment].parishEndpoint(page);
                    const response = await Axios.get(endpoint, { 
                        cancelToken: source.token,
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json'
                        }
                    });
                    globalDispatch({ 
                        type: page === 1 ? 'setParishes' : 'appendParishes', 
                        data: response.data.results 
                    });
                    if (response.data.next) {
                        setTimeout(() => getParishes(page + 1), 100);
                    }
                } catch (error) {
                    if (!Axios.isCancel(error)) console.error("Error fetching parishes:", error);
                }
            }
        }
        getParishes();
        return () => source.cancel();
    }, [selectedAdmLayers, globalDispatch]);

    useEffect(() => {
        const source = Axios.CancelToken.source();
        async function getSzekek(page = 1) {
            if (selectedAdmLayers.includes('szek')) {
                try {
                    const endpoint = config[environment].szekEndpoint(page);
                    const response = await Axios.get(endpoint, { 
                        cancelToken: source.token,
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json'
                        }
                    });
                    globalDispatch({ 
                        type: page === 1 ? 'setSzekek' : 'appendSzekek', 
                        data: response.data.results 
                    });
                    if (response.data.next) {
                        setTimeout(() => getSzekek(page + 1), 100);
                    }
                } catch (error) {
                    if (!Axios.isCancel(error)) console.error("Error fetching szekek:", error);
                }
            }
        }
        getSzekek();
        return () => source.cancel();
    }, [selectedAdmLayers, globalDispatch]);

    useEffect(() => {
        const source = Axios.CancelToken.source();
        async function getMegyek(page = 1) {
            if (selectedAdmLayers.includes('megye')) {
                try {
                    const endpoint = config[environment].megyeEndpoint(page);
                    const response = await Axios.get(endpoint, { 
                        cancelToken: source.token,
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json'
                        }
                    });
                    globalDispatch({ 
                        type: page === 1 ? 'setMegyek' : 'appendMegyek', 
                        data: response.data.results 
                    });
                    if (response.data.next) {
                        setTimeout(() => getMegyek(page + 1), 100);
                    }
                } catch (error) {
                    if (!Axios.isCancel(error)) console.error("Error fetching megyek:", error);
                }
            }
        }
        getMegyek();
        return () => source.cancel();
    }, [selectedAdmLayers, globalDispatch]);

    const handleCategoryToggle = (category) => {
        setSelectedCategories(prev => 
            prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
        );
    };

    const handleKeyToggle = (category) => (key) => {
        setSelectedKeys(prev => ({
            ...prev,
            [category]: {
                ...prev[category],
                [key]: !prev[category][key]
            }
        }));
    };

    const handleAdmLayerToggle = (layer) => {
        setSelectedAdmLayers(prev => 
            prev.includes(layer) ? prev.filter(l => l !== layer) : [...prev, layer]
        );
    };

    if (!globalState.userIsLogged) {
        return (
            <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
                <Paper elevation={3} sx={{ p: 6, borderRadius: 4 }}>
                    <Typography variant="h4" gutterBottom color="primary">
                        GSST - Global Strike-Slip Tectonics
                    </Typography>
                    <Alert severity="warning" sx={{ mb: 4, justifyContent: 'center' }}>
                        {t('GSST.LoginRequired', 'A tartalom megtekintéséhez kérjük jelentkezzen be.')}
                    </Alert>
                    <Button variant="contained" size="large" onClick={() => navigate('/login')}>
                        {t('Header.Login')}
                    </Button>
                </Paper>
            </Container>
        );
    }

    if (!globalState.isSzftMember) {
        return (
            <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
                <Paper elevation={3} sx={{ p: 6, borderRadius: 4 }}>
                    <Typography variant="h4" gutterBottom color="primary">
                        GSST - Global Strike-Slip Tectonics
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 4 }}>
                        {t('GSST.MembershipRequired', 'A tartalom megtekintéséhez igényelje a Székelyföldi Földtani Társulat tagságot.')}
                    </Typography>
                    <Button variant="contained" size="large" onClick={() => navigate('/profile')}>
                        {t('Profile.RequestMembership')}
                    </Button>
                </Paper>
            </Container>
        );
    }

    return (
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative', height: '100%' }}>
            {/* Filter Toggle Button (Mobile) */}
            {isMobile && (
                <IconButton
                    onClick={() => setFilterOpen(true)}
                    sx={{
                        position: 'absolute',
                        top: 10,
                        left: 10,
                        zIndex: 1000,
                        backgroundColor: 'white',
                        boxShadow: 2,
                        '&:hover': { backgroundColor: '#f5f5f5' }
                    }}
                >
                    <FilterListIcon color="primary" />
                </IconButton>
            )}

            <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
                {/* Desktop Filter Sidebar */}
                {!isMobile && (
                    <Paper
                        elevation={2}
                        sx={{
                            width: 300,
                            height: '100%',
                            overflowY: 'auto',
                            zIndex: 10,
                            borderRadius: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.95)'
                        }}
                    >
                        <DesktopFilter
                            categories={{}}
                            selectedCategories={selectedCategories}
                            selectedKeys={selectedKeys}
                            admlayers={gsst_admlayers}
                            selectedAdmLayers={selectedAdmLayers}
                            handleCategoryToggle={handleCategoryToggle}
                            handleKeyToggle={handleKeyToggle}
                            handleAdmLayerToggle={handleAdmLayerToggle}
                        />
                    </Paper>
                )}

                {/* Map Area */}
                <Box sx={{ flexGrow: 1, position: 'relative' }}>
                    <MapComponent 
                        allParishes={selectedAdmLayers.includes('kozsegvaros') ? globalState.allParishes : []}
                        allSzekek={selectedAdmLayers.includes('szek') ? globalState.allSzekek : []}
                        allMegyek={selectedAdmLayers.includes('megye') ? globalState.allMegyek : []}
                        dispatch={globalDispatch} 
                        navigate={navigate}
                        defaultMapType="gsstdomborzat"
                        forcedMaxZoom={12}
                    />
                </Box>
            </Box>

            {/* Mobile Filter Drawer */}
            <MobileDrawer
                filterOpen={filterOpen}
                setFilterOpen={setFilterOpen}
                selectedCategories={selectedCategories}
                selectedKeys={selectedKeys}
                selectedAdmLayers={selectedAdmLayers}
                handleCategoryToggle={handleCategoryToggle}
                handleKeyToggle={handleKeyToggle}
                handleAdmLayerToggle={handleAdmLayerToggle}
                categories={{}}
                admlayers={gsst_admlayers}
            />
        </Box>
    );
}

export default GSST;
